import { computed, effect, inject, Injectable, signal } from '@angular/core'
import { MAP } from '@bases/base.token'
import { HttpService } from '@bases/http.service'
import {
  API_URL,
  MAX_GROSS_SURFACE_AREA_SQKM,
  MAX_GROSS_SURFACE_AREA_SQKM_CM,
  MAX_GROSS_SURFACE_AREA_SQKM_NUTS0,
} from '@core/constants/constant.data'
import {
  COUNTRY,
  HECTARE,
  LAU2,
  NUTS0,
  NUTS1,
  NUTS2,
  NUTS3,
  REGION,
  SUBDIVISION,
} from '@core/constants/scale.data'
import { GeojsonClass } from '@core/models/geojson.model'
import { LayerStandard } from '@core/models/layers.model'
import { isNullOrUndefinedString } from '@services/core.utilities'
import area from '@turf/area'
import booleanIntersects from '@turf/boolean-intersects'
import { buffer } from '@turf/buffer'
import { circle } from '@turf/circle'
import { polygon } from '@turf/helpers'
import * as L from 'leaflet'
import { BehaviorSubject } from 'rxjs'
import { GeoServerService } from './geoserver.service'
import { SelectionScaleService } from './selection-scale.service'

@Injectable({
  providedIn: 'root',
})
export class SelectionToolService extends HttpService {
  private readonly _map = inject<BehaviorSubject<L.DrawMap>>(MAP)
  private readonly _selectionScaleService = inject(SelectionScaleService)
  private readonly _geoServerService = inject(GeoServerService)

  protected _baseUrl = API_URL + '/upload'

  selectedLayers$ = signal<L.GeoJSON[]>([]) //@TODO: rename ? not a layer, more selected areas
  maxGrossSurfaceValue = MAX_GROSS_SURFACE_AREA_SQKM
  maxGrossSurfaceValueCM = MAX_GROSS_SURFACE_AREA_SQKM_CM
  maxGrossSurfaceValueNUTS0 = MAX_GROSS_SURFACE_AREA_SQKM_NUTS0
  hectareAreas$ = signal<L.Layer[]>([])
  nutsIds$ = computed(() => {
    const layers = this.selectedLayers$()
    const ids = new Set<string>()
    const idKeys = ['admin_geom_id', 'comm_id', 'nuts_id', 'gid']
    layers.forEach((layer) => {
      const geoJson = layer.toGeoJSON()
      // Handle FeatureCollection or single Feature
      const features = geoJson.type === 'FeatureCollection' ? geoJson.features : [geoJson]
      features.forEach((feature: any) => {
        for (const key of idKeys) {
          const id = feature?.properties?.[key]
          if (id && typeof id === 'string') {
            ids.add(id)
            break // To prevent adding multiple IDs from the same feature
          }
        }
      })
    })
    return Array.from(ids)
  })

  buttonToolSelected$ = signal<string>('click')

  nbOfElementsSelected$ = computed(() => {
    const scale = this._selectionScaleService.currentScaleLevel$()
    if (scale?.displayName === HECTARE) {
      return this.hectareAreas$().length
    }
    return this.nutsIds$().length
  })

  nbOfDrawnElementSelected$ = computed(() => {
    return this.selectedLayers$().filter(
      (layer: any) => layer.options && layer.options.fillColor === 'red',
    ).length
  })

  netSurface$ = computed(() => {
    const layers = this.selectedLayers$()
    if (!layers.length) return 0

    const featureGroup = L.featureGroup(layers)
    const bounds = featureGroup.getBounds()

    if (JSON.stringify(bounds) !== '{}') {
      const width = bounds.getSouthWest().distanceTo(bounds.getSouthEast())
      const height = bounds.getSouthWest().distanceTo(bounds.getNorthWest())

      const surfaceM2 = width * height
      const surfaceKm2 = surfaceM2 / 1_000_000

      return surfaceKm2 < 0.05 ? surfaceKm2 : Math.round(surfaceKm2 * 100) / 100
    }

    return 0
  })

  selectedSurface$ = computed(() => {
    const layers = this.selectedLayers$()
    if (!layers.length) return 0

    const featureGroup = L.featureGroup(layers)
    const geoJson = featureGroup.toGeoJSON()
    if (!geoJson) return 0

    if (geoJson.type === 'FeatureCollection' && Array.isArray(geoJson.features)) {
      const totalArea = geoJson.features.reduce((sum, feature) => sum + area(feature), 0)
      return totalArea / 1_000_000 // convert m² to km²
    }

    if (geoJson.type === 'Feature') {
      return area(geoJson) / 1_000_000
    }

    return 0
  })

  hectareSelections$ = computed(() => {
    return this._getAreasForPayload(this.hectareAreas$())
  })

  geoJsonSelection$ = computed(() => {
    return this.selectedLayers$().map((layer: any) => layer.toGeoJSON())
  })

  theDrawer: L.Draw.Feature | null = null

  multiSelectionLayers: L.FeatureGroup = new L.FeatureGroup()

  constructor() {
    super()

    effect(() => {
      // Sync multiSelectionLayers with selectedLayers$
      const layers = this.selectedLayers$()
      this.multiSelectionLayers.clearLayers()
      layers.forEach((layer) => this.multiSelectionLayers.addLayer(layer))
    })

    effect(() => {
      // Remove all selection when changing scale level
      this._selectionScaleService.currentScaleLevel$() // Dependency
      this.selectedLayers$.set([])
    })
  }

  drawCreated(event: L.DrawEvents.Created) {
    const drawnLayer: L.Circle | L.CircleMarker | L.Marker | L.Polygon | L.Polyline | L.Rectangle =
      event.layer
    let controlIntersectContainsHectare = false

    // Hectare case
    if (this._selectionScaleService.currentScaleLevel$()?.id === 5) {
      // Use signal instead of multiSelectionLayers
      const featureGroup = L.featureGroup(this.selectedLayers$())
      controlIntersectContainsHectare = this._areLayersOverlapping(featureGroup, drawnLayer)
      if (!controlIntersectContainsHectare) {
        this.drawHectaresLoadingResult(drawnLayer)
      }
    } else {
      const latlngs = this._getLatLngsFromLayer(drawnLayer)
      const currentScaleLevel = this._selectionScaleService.currentScaleLevel$()
      if (currentScaleLevel && currentScaleLevel.geoServer) {
        this._getNutID(latlngs, currentScaleLevel.geoServer).then((result) => {
          this._drawResultBeforeLoadingResult(result)
        })
      }
    }
    this.buttonToolSelected$.set('click')
  }

  getNutsOrAreas() {
    if (this._selectionScaleService.currentScaleLevel$()?.displayName === HECTARE) {
      return this._getAreasForPayload(this.hectareAreas$())
    }
    return this.nutsIds$()
  }

  removeLayerFromMultiSelectionLayers(layer: any) {
    const id_selection = this._getSelectionIdFromLayer(layer)
    // Remove by selection ID or Leaflet ID
    this.selectedLayers$.set(
      this.selectedLayers$().filter((existingLayer) => {
        if (this._getSelectionIdFromLayer(existingLayer) === id_selection) return false
        const layerLeafletId = (layer as any)?._leaflet_id
        const existingLeafletId = (existingLayer as any)?._leaflet_id
        if (
          typeof layerLeafletId !== 'undefined' &&
          typeof existingLeafletId !== 'undefined' &&
          layerLeafletId === existingLeafletId
        )
          return false
        return true
      }),
    )
  }

  containLayer(layer: any): boolean {
    if (!isNullOrUndefinedString(layer?._leaflet_id)) {
      if (this.nutsIds$().includes(this._getSelectionIdFromLayer(layer))) {
        return true
      }
    }
    return false
  }

  //@todo, make computed
  clearAll() {
    this.selectedLayers$.set([])
    this.hectareAreas$.set([])
  }

  activateDrawTool() {
    const myMap = this._map.getValue()

    switch (this.buttonToolSelected$()) {
      case 'rectangle':
        this.theDrawer = new L.Draw.Rectangle(myMap, <L.DrawOptions.RectangleOptions>{
          shapeOptions: { showArea: false },
        })
        break
      case 'circle':
        this.theDrawer = new L.Draw.Circle(myMap)
        break

      case 'polygon':
        this.theDrawer = new L.Draw.Polygon(myMap)
        break
      default:
        break
    }
    this.theDrawer?.enable()
  }

  deactivateDrawTool() {
    this.theDrawer?.disable()
    this.theDrawer = null
  }

  private _getNutID(latlngs: L.LatLng[], stringLayerType: string) {
    const epsg = '4326'
    const coordinate = this._latLngsToLocationString(latlngs)
    let url = this._geoServerService.getGeoServerUrl(
      stringLayerType,
      '',
      `WITHIN(geom,polygon((${coordinate})))`,
      '',
      epsg,
    )
    return new Promise((resolve) => {
      this._http.get(url).subscribe({
        next: (result) => {
          return resolve(result)
        },
        error: (error) => {
          console.error(error)
          // To override 404 message of geoserver
          this._toasterService.openSnackBarError('Error fetching selectable areas')
          resolve(null)
        },
      })
    })
  }

  setAreas() {
    this.hectareAreas$.set(this.selectedLayers$())
  }

  drawHectaresLoadingResult(layer: any) {
    if (!this.selectedLayers$().includes(layer)) {
      if (layer instanceof L.Circle) {
        const center = [layer.getLatLng().lng, layer.getLatLng().lat]
        const radiusInKm = layer.getRadius() / 1000

        const polygonGeoJSON = circle(center, radiusInKm, { steps: 64, units: 'kilometers' })
        const coordinates: L.LatLngExpression[] = polygonGeoJSON.geometry.coordinates[0].map(
          (coord: number[]) => L.latLng(coord[1], coord[0]),
        )

        const polygonLayer = L.polygon(coordinates)

        layer = polygonLayer
      }
      this.selectedLayers$.set([...this.selectedLayers$(), layer])
      this.setAreas()
      layer.on('click', () => {
        if (layer.options.fillColor === null) {
          layer.setStyle({ fillColor: 'red' })
        } else {
          layer.setStyle({ fillColor: null })
        }
      })
    }
  }

  private _drawResultBeforeLoadingResult(result: any) {
    if (isNullOrUndefinedString(result) === false) {
      if (result.features.length <= 0) {
        this._toasterService.openSnackBar(
          'No ' +
            this._selectionScaleService.currentScaleLevel$()?.displayName +
            ' fully included in drawn selection',
        )
      }

      for (const feature of result.features) {
        const selectionID = this._getSelectionIDFromGeoJsonLayer(feature)
        // Check if selectionID already exists in selectedLayers$
        const alreadySelected = this.selectedLayers$().some((layer) => {
          const geoJson = layer.toGeoJSON()
          const features = geoJson.type === 'FeatureCollection' ? geoJson.features : [geoJson]
          return features.some((f: any) => this._getSelectionIDFromGeoJsonLayer(f) === selectionID)
        })
        if (!alreadySelected) {
          const areaNutsSelectedLayer = L.geoJson(feature)
          this.selectedLayers$.set([...this.selectedLayers$(), areaNutsSelectedLayer])
        }
      }
    }
  }

  addToMultiSelectionLayers(layer: any) {
    if (isNullOrUndefinedString(layer) === false) {
      const selection_id = this._getSelectionIdFromLayer(layer)
      // Check if selection_id already exists in selectedLayers$
      const alreadySelected = this.selectedLayers$().some((existingLayer) => {
        return this._getSelectionIdFromLayer(existingLayer) === selection_id
      })
      if (!alreadySelected) {
        this.selectedLayers$.set([...this.selectedLayers$(), layer])
      }
    }
  }

  addHectareToMultiSelectionLayers(layer: any) {
    if (isNullOrUndefinedString(layer) === false) {
      this.selectedLayers$.set([...this.selectedLayers$(), layer])
    }
  }

  deleteSelectedAreas() {
    this.selectedLayers$.set(
      this.selectedLayers$().filter((layer: any) => layer.options.fillColor !== 'red'),
    )
    this.setAreas()
  }

  exportLayerSelection(layer: LayerStandard): Promise<Blob> {
    const currentScaleLevel = this._selectionScaleService.currentScaleLevel$()

    let nutsOrAreas: Array<string | any>
    let isNuts: boolean = true
    const layerName = layer.workspacename
    const dataType = layer.dataType
    const displayName = currentScaleLevel?.displayName

    if (
      displayName &&
      [LAU2, NUTS3, NUTS2, NUTS1, NUTS0, REGION, COUNTRY, SUBDIVISION].indexOf(displayName) > -1
    ) {
      nutsOrAreas = this.nutsIds$()
    } else if (displayName && displayName === HECTARE) {
      nutsOrAreas = this._getAreasForPayload(this.hectareAreas$())
      isNuts = false
    } else {
      return Promise.reject('Unknown scale level')
    }

    const url = this._baseUrl + `/export/${dataType}/${isNuts ? 'nuts' : 'hectare'}`
    const layerToExport = {
      layer: layerName,
      [isNuts ? 'nuts' : 'areas']: nutsOrAreas,
      scaleLevel: currentScaleLevel.apiName,
    }

    return this.POSTtoGetBlob(layerToExport, url)
  }

  private _isSurfaceValid(maxValue: number): boolean {
    const netSurface = this.netSurface$()
    return netSurface > 0 && netSurface <= maxValue
  }

  private _isSurfaceTooSmall(): boolean {
    const netSurface = this.netSurface$()
    return netSurface > 0 && netSurface < 0.005
  }

  isCMSelectionSurfaceCorrect(): boolean {
    const currentScale = this._selectionScaleService.currentScaleLevel$()?.displayName
    const maxSurfaceValue =
      currentScale === 'NUTS 0' ? this.maxGrossSurfaceValueNUTS0 : this.maxGrossSurfaceValueCM
    return this._isSurfaceValid(maxSurfaceValue)
  }

  isCMSelectionSurfaceTooSmall(): boolean {
    return this._isSurfaceTooSmall()
  }

  isLayerSelectionDownloadSurfaceCorrect(): boolean {
    return this._isSurfaceValid(this.maxGrossSurfaceValue)
  }

  private _getSelectionIdFromLayer(layer: L.GeoJSON): any {
    const geojsonLayer: any = <any>layer
    const geoJson: GeojsonClass = geojsonLayer.toGeoJSON()
    const feature = geoJson.features[0]

    const id_selection = this._getSelectionIDFromGeoJsonLayer(feature)
    return id_selection
  }

  private _areLayersOverlapping(
    baseLayer: L.FeatureGroup,
    drawLayer: L.Circle | L.CircleMarker | L.Marker | L.Polygon | L.Polyline | L.Rectangle,
  ): boolean {
    // Convert drawLayer to GeoJSON
    let drawGeoJson: any
    if ('type' in drawLayer && drawLayer.type === 'LineString') {
      drawGeoJson = drawLayer
    } else if (drawLayer instanceof L.Circle) {
      drawGeoJson = this._circleToGeoJSON(drawLayer)
    } else {
      drawGeoJson = drawLayer.toGeoJSON()
    }

    const baseGeoJson: any = baseLayer.toGeoJSON()

    // If baseGeoJson is a FeatureCollection, check each feature
    if (baseGeoJson.type === 'FeatureCollection') {
      return baseGeoJson.features.some((feature: any) => booleanIntersects(feature, drawGeoJson))
    }

    // Otherwise, just check the single feature
    return booleanIntersects(baseGeoJson, drawGeoJson)
  }
  private _circleToGeoJSON(layer: L.Circle) {
    return {
      type: 'Feature',
      properties: {},
      geometry: {
        type: 'Polygon',
        coordinates: [this._latLngsToCoords(this._getLatLngsFromCircle(layer))],
      },
    }
  }
  private _latLngsToCoords(arrLatlng: L.LatLng[]) {
    const coords: number[][] = []
    arrLatlng.forEach(function (latlng) {
      coords.push([latlng.lng, latlng.lat])
    }, this)
    return coords
  }

  /**
   * Draw method of the activated selection tool
   */
  selectDrawTool(button: string) {
    this.buttonToolSelected$.set(button)

    // clean current drawing if new tool selected and draw started
    if (this.theDrawer) {
      this.deactivateDrawTool()
    }

    if (this.buttonToolSelected$() !== 'click') {
      this.activateDrawTool()
    }
  }

  private _getSelectionIDFromGeoJsonLayer(feature: any): string {
    if (!feature || !feature.properties) return ''

    const keys = ['admin_geom_id', 'nuts_id', 'comm_id', 'gid_2', 'gid_1', 'gid_0', 'gid']

    for (const key of keys) {
      const value = feature.properties[key]
      if (!isNullOrUndefinedString(value)) {
        return value
      }
    }

    return ''
  }

  private _getAreasForPayload(areas: L.Layer[]) {
    const ar: { points: L.LatLng[] }[] = []
    areas.map((layer: L.Layer) => {
      if (layer instanceof L.Circle) {
        // From snapshot
        ar.push({ points: this._getLatLngsFromCircle(layer) })
      } else if (layer instanceof L.Polygon) {
        ar.push({ points: this._getLatLngsFromPolygon(layer) })
      }
    })
    return ar
  }

  private _getLatLngsFromPolygon(layer: L.Polygon | L.Rectangle): L.LatLng[] {
    // For both Polygon and Rectangle, getLatLngs()[0] gives the outer ring
    const latlngs = layer.getLatLngs()
    return Array.isArray(latlngs[0]) ? (latlngs[0] as L.LatLng[]) : (latlngs as L.LatLng[])
  }

  private _latLngsToLocationString(latlngs: L.LatLng[]): string {
    if (!latlngs || latlngs.length === 0) return ''
    let locations = latlngs.map((ll) => `${ll.lat} ${ll.lng}`).join(',')
    // Close the polygon if needed
    if (latlngs.length > 0) {
      locations += `,${latlngs[0].lat} ${latlngs[0].lng}`
    }
    return locations
  }

  // Helper to extract L.LatLng[] from any supported layer
  private _getLatLngsFromLayer(
    layer: L.Circle | L.CircleMarker | L.Marker | L.Polygon | L.Polyline | L.Rectangle,
  ): L.LatLng[] {
    if (layer instanceof L.Circle) {
      return this._getLatLngsFromCircle(layer)
    } else if (layer instanceof L.CircleMarker || layer instanceof L.Marker) {
      return [layer.getLatLng()]
    } else if (layer instanceof L.Polygon || layer instanceof L.Polyline) {
      const latlngs = layer.getLatLngs()
      return Array.isArray(latlngs[0]) ? (latlngs[0] as L.LatLng[]) : (latlngs as L.LatLng[])
    }
    return []
  }

  private _getLatLngsFromCircle(layer: L.Circle): L.LatLng[] {
    const origin = layer.getLatLng()
    const radius = layer.getRadius() / 1000 // Turf expects kilometers
    const steps = 60

    const turfPoly = circle([origin.lng, origin.lat], radius, { steps, units: 'kilometers' })
    const coords = turfPoly.geometry.coordinates[0]
    // Convert Turf [lng, lat] to Leaflet L.LatLng
    return coords.map(([lng, lat]) => L.latLng(lat, lng))
  }

  drawSelectionShapeFromFile(geoJson: any) {
    const gapDistance = 0.01

    geoJson.features.forEach((feature: GeoJSON.Feature) => {
      const geom = feature.geometry
      if (geom.type == 'MultiPolygon') {
        geom.coordinates.forEach((coord: GeoJSON.Position[][]) => {
          const poly = L.polygon(L.GeoJSON.coordsToLatLngs(coord[0]))
          this.drawHectaresLoadingResult(poly)
        })
      } else if (geom.type == 'Polygon') {
        const polygonGeoJSON = polygon(geom.coordinates)
        const bufferedFeature = buffer(polygonGeoJSON, -gapDistance, { units: 'meters' })
        if (bufferedFeature) {
          const latLong = L.GeoJSON.coordsToLatLngs(bufferedFeature.geometry.coordinates, 1)
          const poly = L.polygon(latLong)
          this.drawHectaresLoadingResult(poly)
        }
      } else {
        console.warn('Shape not supported:', geom.type)
      }
    })
  }
}
